import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Briefcase, CheckCircle2, Loader2, MapPin, Paperclip, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { Header } from "@/pages/home/Chrome";
import { cn } from "@/lib/utils";

const field = "h-11 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-base text-navy-900 outline-none focus:border-navy-500 focus:ring-2 focus:ring-navy-100 sm:text-sm";
const lbl = "mb-1.5 block text-xs font-medium text-slate-500";

const REQUIRED_Q = [
  ["computerUse", "I can use computers, email, and online tools to research, plan, and book travel arrangements."],
  ["membershipFee", "I'm willing to pay the standard agency membership fee of $32/month (training, booking tools, and coverage)."],
];
const PREFERRED_Q = [
  ["salesHospitality", "I have experience in sales or hospitality roles."],
  ["customerService", "I have experience providing customer service to clients or customers."],
  ["remoteWork", "I have experience working independently in a remote or flexible-schedule role."],
];

function QuestionRow({ q, required, value, onChange, testid }) {
  return (
    <div className="flex flex-col gap-2 rounded-xl border border-slate-200 p-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4" data-testid={testid}>
      <p className="text-sm text-navy-900">{q}{required && <span className="text-red-500"> *</span>}</p>
      <div className="flex shrink-0 gap-2">
        {[[true, "Yes"], [false, "No"]].map(([val, l]) => (
          <button type="button" key={l} onClick={() => onChange(val)} data-testid={`${testid}-${l.toLowerCase()}`} className={cn("h-8 rounded-lg px-4 text-sm font-medium transition-colors", value === val ? (val ? "bg-emerald-600 text-white" : "bg-red-600 text-white") : "bg-slate-100 text-slate-500 hover:bg-slate-200")}>{l}</button>
        ))}
      </div>
    </div>
  );
}

function Success({ onReset, onClose }) {
  return (
    <div className="grid place-items-center py-8 text-center" data-testid="careers-success">
      <CheckCircle2 className="h-12 w-12 text-emerald-500" />
      <h3 className="mt-4 font-display text-xl font-bold text-navy-900">Application received!</h3>
      <p className="mt-2 max-w-sm text-sm text-slate-600">Thank you for your interest in Topsail Travel. Our team will review your application and reach out if there's a match.</p>
      {onClose
        ? <button onClick={onClose} className="mt-5 inline-flex h-10 items-center rounded-xl bg-navy-900 px-4 text-sm font-semibold text-white hover:bg-navy-800" data-testid="careers-close">Done</button>
        : <button onClick={onReset} className="mt-5 inline-flex h-10 items-center rounded-xl border border-slate-200 px-4 text-sm font-semibold text-navy-900 hover:bg-slate-50" data-testid="careers-another">Submit another</button>}
    </div>
  );
}

function ApplyForm({ posting, onClose, compact = false }) {
  const fileRef = useRef(null);
  const [v, setV] = useState({ name: "", email: "", phone: "", linkedin: "", yearsExperience: "", message: "" });
  const [resume, setResume] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [qual, setQual] = useState({ computerUse: null, membershipFee: null, salesHospitality: null, customerService: null, remoteWork: null });
  const set = (k) => (e) => setV({ ...v, [k]: e.target.value });

  const pickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) return toast.error("File must be 15MB or smaller");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/careers/upload-resume", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResume(data);
    } catch (err) { toast.error(errMsg(err)); }
    setUploading(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!v.name.trim() || !v.email.trim()) return toast.error("Please add your name and email");
    if (qual.computerUse === null || qual.membershipFee === null) return toast.error("Please answer the required questions (marked *)");
    setBusy(true);
    try {
      await api.post("/careers/apply", { ...v, postingId: posting?.id || null, resumeKey: resume?.key || null, resumeName: resume?.name || null, qualifications: qual });
      setSent(true);
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };

  if (sent) return <Success onClose={onClose} onReset={() => { setSent(false); setV({ name: "", email: "", phone: "", linkedin: "", yearsExperience: "", message: "" }); setResume(null); setQual({ computerUse: null, membershipFee: null, salesHospitality: null, customerService: null, remoteWork: null }); }} />;

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="careers-apply-form">
      {!compact && (
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#C9A96E]">Apply</p>
          <h3 className="font-display text-xl font-bold text-navy-900">Join our team</h3>
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        <label><span className={lbl}>Full name *</span><input className={field} value={v.name} onChange={set("name")} data-testid="careers-name" /></label>
        <label><span className={lbl}>Email *</span><input type="email" className={field} value={v.email} onChange={set("email")} data-testid="careers-email" /></label>
        <label><span className={lbl}>Phone</span><input className={field} value={v.phone} onChange={set("phone")} data-testid="careers-phone" /></label>
        <label><span className={lbl}>Years in travel industry</span><input className={field} value={v.yearsExperience} onChange={set("yearsExperience")} placeholder="e.g. 3" data-testid="careers-years" /></label>
        <label className="sm:col-span-2"><span className={lbl}>LinkedIn or website</span><input className={field} value={v.linkedin} onChange={set("linkedin")} placeholder="https://" data-testid="careers-linkedin" /></label>
        <label className="sm:col-span-2"><span className={lbl}>Anything you'd like us to know (optional)</span><textarea rows={3} className={cn(field, "h-auto py-2.5")} value={v.message} onChange={set("message")} data-testid="careers-message" /></label>
      </div>
      <div className="space-y-2.5">
        <span className={lbl}>A few quick questions</span>
        {REQUIRED_Q.map(([k, q]) => <QuestionRow key={k} q={q} required value={qual[k]} onChange={(val) => setQual({ ...qual, [k]: val })} testid={`careers-q-${k}`} />)}
        {PREFERRED_Q.map(([k, q]) => <QuestionRow key={k} q={q} value={qual[k]} onChange={(val) => setQual({ ...qual, [k]: val })} testid={`careers-q-${k}`} />)}
      </div>
      <div>
        <span className={lbl}>Resume / CV (PDF or Word)</span>
        <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" onChange={pickFile} className="hidden" data-testid="careers-resume-input" />
        {resume ? (
          <div className="flex items-center justify-between gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-3.5 py-2.5" data-testid="careers-resume-chip">
            <span className="flex min-w-0 items-center gap-2 text-sm text-navy-900"><Paperclip className="h-4 w-4 shrink-0 text-emerald-600" /><span className="truncate">{resume.name}</span></span>
            <button type="button" onClick={() => setResume(null)} className="text-slate-400 hover:text-red-600"><X className="h-4 w-4" /></button>
          </div>
        ) : (
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="careers-resume-btn" className="flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 text-sm font-medium text-slate-500 hover:border-navy-400 hover:text-navy-900">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}{uploading ? "Uploading…" : "Upload resume"}
          </button>
        )}
      </div>
      <button disabled={busy || uploading} data-testid="careers-submit" className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-navy-900 text-sm font-semibold text-white transition-colors hover:bg-navy-800 disabled:opacity-60">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}Submit application
      </button>
    </form>
  );
}

function RoleModal({ posting, onOpenChange }) {
  return (
    <Dialog open={!!posting} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto p-0" data-testid="careers-role-modal">
        {posting && (
          <>
            <div className="border-b border-slate-100 bg-[#0f2033] px-6 py-6 text-white sm:px-8">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#C9A96E]">Open position</p>
              <h2 className="mt-1 font-display text-2xl font-bold">{posting.title}</h2>
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-white/70">
                {posting.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{posting.location}</span>}
                {posting.type && <span>{posting.type}</span>}
              </div>
            </div>
            <div className="px-6 py-6 sm:px-8">
              {posting.description && (
                <div className="mb-6">
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">About the role</h3>
                  <p className="whitespace-pre-line text-sm leading-relaxed text-slate-600">{posting.description}</p>
                </div>
              )}
              <div className="border-t border-slate-100 pt-5">
                <h3 className="mb-4 font-display text-lg font-bold text-navy-900">Apply for this role</h3>
                <ApplyForm posting={posting} onClose={() => onOpenChange(false)} compact />
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Careers() {
  const [postings, setPostings] = useState(null);
  const [modal, setModal] = useState(null);
  const formRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => { api.get("/careers/postings").then(({ data }) => setPostings(data.items)).catch(() => setPostings([])); }, []);
  const scrollToGeneral = () => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div className="app-shell scroll-area min-h-screen bg-[#F5F7FA]" data-testid="careers-page">
      <Header onFindAgent={() => navigate("/")} />

      <main className="mx-auto max-w-4xl px-5 py-10 sm:px-8 sm:py-14">
        <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#C9A96E]">Careers at Topsail Travel</p>
        <h1 className="mt-2 font-display text-3xl font-bold text-navy-900 sm:text-5xl">Help people see the world.</h1>
        <p className="mt-4 max-w-2xl text-sm leading-relaxed text-slate-600 sm:text-base">We're a growing, remote-friendly travel agency planning cruises, Disney escapes and bucket-list journeys. If you love travel and taking care of people, we'd love to meet you — apply below.</p>

        <div className="mt-10 grid gap-8 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400">Open roles</h2>
            <div className="mt-4 space-y-3" data-testid="careers-openings">
              {postings === null ? (
                <div className="grid place-items-center rounded-2xl border border-slate-200 bg-white py-12"><Loader2 className="h-5 w-5 animate-spin text-navy-700" /></div>
              ) : postings.length === 0 ? (
                <div className="rounded-2xl border border-slate-200 bg-white p-5 text-sm text-slate-500" data-testid="careers-no-openings">No specific openings right now — but we're always glad to meet great people. Send a general application and we'll be in touch.</div>
              ) : postings.map((p) => (
                <div key={p.id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`careers-posting-${p.id}`}>
                  <div className="flex items-center gap-2"><Briefcase className="h-4 w-4 text-[#C9A96E]" /><p className="text-sm font-semibold text-navy-900">{p.title}</p></div>
                  <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
                    {p.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{p.location}</span>}
                    {p.type && <span>{p.type}</span>}
                  </div>
                  {p.description && <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-slate-600">{p.description}</p>}
                  <button onClick={() => setModal(p)} data-testid={`careers-apply-${p.id}`} className="mt-3 inline-flex h-9 items-center rounded-lg bg-navy-900 px-4 text-sm font-semibold text-white hover:bg-navy-800">View role &amp; apply</button>
                </div>
              ))}
              {postings && postings.length > 0 && (
                <button onClick={scrollToGeneral} data-testid="careers-general-apply" className="w-full rounded-2xl border border-dashed border-slate-300 p-4 text-left text-sm font-medium text-slate-500 hover:border-navy-400 hover:text-navy-900">Don't see a fit? Send a general application →</button>
              )}
            </div>
          </div>

          <div className="lg:col-span-3" ref={formRef}>
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-7">
              <ApplyForm posting={null} />
            </div>
          </div>
        </div>
      </main>

      <RoleModal posting={modal} onOpenChange={(o) => !o && setModal(null)} />
    </div>
  );
}
